import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ContentBlockWrapper, ContentItem, SimplifiedDocumentData } from './editor/ContentBlockWrapper';
import { CommandMenu } from '../editor/CommandMenu';
import { toast } from 'sonner';
import { generateId } from '@/lib/utils/uuid';

interface SimplifiedDocumentWriterProps {
  initialData?: SimplifiedDocumentData;
  onChange?: (data: SimplifiedDocumentData) => void;
  className?: string;
  onDocumentCreated?: (documentId: string) => void;
  onGlossaryItemEdit?: (glossaryItemId: string) => void;
  onDocumentDisplayEdit?: (documentId: string) => void;
  onDocumentDisplayNavigate?: (documentId: string) => void;
}

export const SimplifiedDocumentWriter: React.FC<SimplifiedDocumentWriterProps> = React.memo(({
  initialData,
  onChange,
  className = "",
  onDocumentCreated,
  onGlossaryItemEdit,
  onDocumentDisplayEdit,
  onDocumentDisplayNavigate
}) => {
  // Initialize with simplified data structure
  const [documentData, setDocumentData] = useState<SimplifiedDocumentData>(
    initialData || {
      content: [{ id: '1', type: 'text', content: '' }],
      lastSaved: new Date().toISOString(),
      widgetData: {}
    }
  );
  
  const [activeItemId, setActiveItemId] = useState('1');
  const isInitialized = useRef(false);
  const lastInitialDataRef = useRef<string>('');
  
  // Command menu state
  const [commandMenu, setCommandMenu] = useState<{
    isOpen: boolean;
    itemId: string;
    position: { x: number; y: number };
  }>({
    isOpen: false,
    itemId: '',
    position: { x: 0, y: 0 }
  });


  // Handle content updates
  const handleContentUpdate = useCallback((id: string, content: string) => {
    setDocumentData(prev => {
      const newContent = prev.content.map(item => 
        item.id === id ? { ...item, content } : item
      );
      const newData = {
        ...prev,
        content: newContent,
        lastSaved: new Date().toISOString()
      };
      onChange?.(newData);
      return newData;
    });
  }, [onChange]);

  // Handle Enter key - create new item below
  const handleEnter = useCallback((id: string) => {
    const currentIndex = documentData.content.findIndex(item => item.id === id);
    const newItem: ContentItem = {
      id: generateId(),
      type: 'text',
      content: ''
    };
    
    setDocumentData(prev => {
      const newContent = [...prev.content];
      newContent.splice(currentIndex + 1, 0, newItem);
      const newData = {
        ...prev,
        content: newContent,
        lastSaved: new Date().toISOString()
      };
      onChange?.(newData);
      return newData;
    });
    
    setActiveItemId(newItem.id);
  }, [documentData.content, onChange]);

  // Handle Backspace key - delete current item if empty
  const handleBackspace = useCallback((id: string) => {
    if (documentData.content.length === 1) return; // Don't delete the last item
    
    const currentIndex = documentData.content.findIndex(item => item.id === id);
    if (currentIndex === 0) return; // Don't delete the first item
    
    const prevItem = documentData.content[currentIndex - 1];
    setDocumentData(prev => {
      const newContent = prev.content.filter(item => item.id !== id);
      const newData = {
        ...prev,
        content: newContent,
        lastSaved: new Date().toISOString()
      };
      onChange?.(newData);
      return newData;
    });
    setActiveItemId(prevItem.id);
  }, [documentData.content, onChange]);

  // Handle focus events
  const handleFocus = useCallback((id: string) => {
    setActiveItemId(id);
  }, []);

  // Handle arrow key navigation
  const handleArrowUp = useCallback((id: string) => {
    const currentIndex = documentData.content.findIndex(item => item.id === id);
    if (currentIndex > 0) {
      setActiveItemId(documentData.content[currentIndex - 1].id);
    }
  }, [documentData.content]);

  const handleArrowDown = useCallback((id: string) => {
    const currentIndex = documentData.content.findIndex(item => item.id === id);
    if (currentIndex < documentData.content.length - 1) {
      setActiveItemId(documentData.content[currentIndex + 1].id);
    }
  }, [documentData.content]);

  // Handle command input
  const handleCommand = useCallback((id: string, command: string) => {
    if (command === '/') {
      const element = document.activeElement as HTMLTextAreaElement;
      if (element) {
        const rect = element.getBoundingClientRect();
        setCommandMenu({
          isOpen: true,
          itemId: id,
          position: { x: rect.left, y: rect.bottom + 8 }
        });
      }
    }
  }, []);

  // Handle command selection
  const handleCommandSelect = useCallback((command: string) => {
    const itemId = commandMenu.itemId;
    const item = documentData.content.find(i => i.id === itemId);
    
    if (!item) return;

    if (command === 'image') {
      // Convert to image type
      setDocumentData(prev => {
        const newContent = prev.content.map(i => 
          i.id === itemId 
            ? { ...i, type: 'image', content: '', metadata: {} }
            : i
        );
        const newData = {
          ...prev,
          content: newContent,
          lastSaved: new Date().toISOString()
        };
        onChange?.(newData);
        return newData;
      });
    } else {
      // Convert to other type
      const newContent = item.content.slice(0, -1); // Remove the '/' character
      setDocumentData(prev => {
        const newContentArray = prev.content.map(i => 
          i.id === itemId 
            ? { ...i, type: command, content: newContent }
            : i
        );
        const newData = {
          ...prev,
          content: newContentArray,
          lastSaved: new Date().toISOString()
        };
        onChange?.(newData);
        return newData;
      });
    }
    
    setCommandMenu({ isOpen: false, itemId: '', position: { x: 0, y: 0 } });
    toast.success(`Block converted to ${command}`);
  }, [commandMenu.itemId, documentData.content, onChange]);

  // Handle item deletion
  const handleDelete = useCallback((id: string) => {
    if (documentData.content.length === 1) return; // Don't delete the last item
    
    const currentIndex = documentData.content.findIndex(item => item.id === id);
    const nextItem = documentData.content[currentIndex + 1] || documentData.content[currentIndex - 1];
    
    setDocumentData(prev => {
      const newContent = prev.content.filter(item => item.id !== id);
      const newData = {
        ...prev,
        content: newContent,
        lastSaved: new Date().toISOString()
      };
      onChange?.(newData);
      return newData;
    });
    
    if (nextItem) {
      setActiveItemId(nextItem.id);
    }
    toast.success('Block deleted');
  }, [documentData.content, onChange]);

  // Handle external data updates
  useEffect(() => {
    if (initialData) {
      const initialDataString = JSON.stringify(initialData);
      
      if (initialDataString !== lastInitialDataRef.current) {
        setDocumentData(initialData);
        lastInitialDataRef.current = initialDataString;
        isInitialized.current = true;
        
        // Set the first item as active if there are items
        if (initialData.content.length > 0) {
          setActiveItemId(initialData.content[0].id);
        }
      }
    }
  }, [initialData]);

  return (
    <div className={`w-full h-full ${className}`}>
      <div className="min-h-full bg-editor-bg">
        <div className="max-w-4xl mx-auto px-6 pt-12 pb-[1000px]">
          {/* Content items container */}
          <div className="space-y-3">
            {documentData.content.map((item) => (
              <ContentBlockWrapper
                key={item.id}
                item={item}
                isActive={activeItemId === item.id}
                onUpdate={handleContentUpdate}
                onDelete={handleDelete}
                onFocus={handleFocus}
                onEnter={handleEnter}
                onBackspace={handleBackspace}
                onArrowUp={handleArrowUp}
                onArrowDown={handleArrowDown}
                onCommand={handleCommand}
              />
            ))}
          </div>

          {/* Command menu */}
          <CommandMenu
            isOpen={commandMenu.isOpen}
            onSelect={handleCommandSelect}
            onClose={() => setCommandMenu({ isOpen: false, itemId: '', position: { x: 0, y: 0 } })}
            position={commandMenu.position}
            customCommands={[]} // Add custom commands here if needed
          />
        </div>
      </div>
    </div>
  );
}); 